/**
 * Main App Component - React Router Configuration
 *
 * ARCHITECTURE NOTE:
 * This React app is the primary application served at all routes (via Firebase routing).
 * The pre-React legacy HTML app lives in git history only (removed 2026-07, was /archive/).
 */

import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { UsageTracker } from '@/lib/usage/UsageTracker';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthGuard } from './components/AuthGuard';
import { RequireAdmin } from './components/RequireAdmin';
import { RequireLite } from './components/lite/RequireLite';
import { RouteFallback } from './components/RouteFallback';
import { RequireProposalBuilderV2 } from './components/proposals-v2/builder/RequireProposalBuilderV2';
import { useTenant } from '@/hooks/useTenant';
import { lazy, Suspense, useEffect } from 'react';


// Settings Module
// Landing Page (Login/Auth Screen)
import { LandingPage } from './pages/LandingPage';
import { AuthVerifyPage } from './pages/AuthVerifyPage';

// Module Pages


// Project Management — Projects v2 owns /home/projects after the June 2026
// cutover. ProjectsComingSoonPage + the legacy ProjectDetailPage are no longer
// routed (files retained for reference).

// Deal Workspace

// Legacy Database

// Office Dashboard Shell & Pages
import { OfficeLayout } from './components/office/OfficeLayout';
import OfficeDashboardV2Page from './pages/OfficeDashboardV2Page';
import { OtisWidget } from './components/otis/OtisWidget';
import { OtisVisibilityProvider } from './components/otis/OtisVisibilityContext';
import { AuthSessionProvider } from './context/AuthSessionContext';
import { FinancialVisibilityProvider } from './context/FinancialVisibilityContext';
import { FinancialAccessGate } from './components/FinancialAccessGate';
import { RequireFeature } from './components/RequireFeature';

// ---------------------------------------------------------------------------
// Route-level code splitting
//
// Every route below was a static import, which put all ~2,400 modules into a
// single 8.7 MB `dashboard` chunk — the whole app downloaded before the first
// screen painted, and the same chunk that OOM'd the production build.
//
// Kept STATIC above: the error boundary, the auth guards, the landing page and
// the two shells. Those are on the critical path to first paint, so splitting
// them would only add a round trip to the first screen anyone sees.
//
// Everything else is lazy. `React.lazy` needs a default export, hence the
// `.then(m => ({ default: m.X }))` unwrap for the named ones.
//
// Chunk-load failures after a deploy are already handled globally — see
// isChunkLoadError / nukeAndReload in src/main.tsx, which catch the
// 'Failed to fetch dynamically imported module' rejection and hard-recover.
// ---------------------------------------------------------------------------

const Provisioning = lazy(() => import('./pages/Provisioning').then((m) => ({ default: m.Provisioning })));
const QuoteReviewPage = lazy(() => import('./pages/QuoteReviewPage').then((m) => ({ default: m.QuoteReviewPage })));
const AdminSeedPage = lazy(() => import('./pages/AdminSeedPage').then((m) => ({ default: m.AdminSeedPage })));
const PlatformLayout = lazy(() => import('./pages/platform/PlatformLayout').then((m) => ({ default: m.PlatformLayout })));
const PlatformOverviewPage = lazy(() => import('./pages/platform/PlatformOverviewPage').then((m) => ({ default: m.PlatformOverviewPage })));
const PlatformTenantsPage = lazy(() => import('./pages/platform/PlatformTenantsPage').then((m) => ({ default: m.PlatformTenantsPage })));
const PlatformTenantDetailPage = lazy(() => import('./pages/platform/PlatformTenantDetailPage').then((m) => ({ default: m.PlatformTenantDetailPage })));
const PlatformBillingPage = lazy(() => import('./pages/platform/PlatformBillingPage').then((m) => ({ default: m.PlatformBillingPage })));
const PlatformUsagePage = lazy(() => import('./pages/platform/PlatformUsagePage').then((m) => ({ default: m.PlatformUsagePage })));
const PlatformSecurityPage = lazy(() => import('./pages/platform/PlatformSecurityPage').then((m) => ({ default: m.PlatformSecurityPage })));
const ProposalTemplatesListPage = lazy(() => import('./pages/admin/ProposalTemplatesListPage').then((m) => ({ default: m.ProposalTemplatesListPage })));
const ProposalTemplateEditPage = lazy(() => import('./pages/admin/ProposalTemplateEditPage').then((m) => ({ default: m.ProposalTemplateEditPage })));
const ProposalSenderDomainPage = lazy(() => import('./pages/admin/ProposalSenderDomainPage').then((m) => ({ default: m.ProposalSenderDomainPage })));
const ProposalSettingsLayout = lazy(() => import('./pages/admin/ProposalSettingsLayout').then((m) => ({ default: m.ProposalSettingsLayout })));
const ProposalEmailsPage = lazy(() => import('./pages/admin/ProposalEmailsPage').then((m) => ({ default: m.ProposalEmailsPage })));
const ProposalBrandingPage = lazy(() => import('./pages/admin/ProposalBrandingPage').then((m) => ({ default: m.ProposalBrandingPage })));
const ProposalMigrationPage = lazy(() => import('./pages/admin/ProposalMigrationPage').then((m) => ({ default: m.ProposalMigrationPage })));
const ProposalComparisonPage = lazy(() => import('./pages/admin/ProposalComparisonPage').then((m) => ({ default: m.ProposalComparisonPage })));
const EmailAnalyticsPage = lazy(() => import('./pages/admin/EmailAnalyticsPage'));
const ProposalAssetsPage = lazy(() => import('./pages/admin/ProposalAssetsPage').then((m) => ({ default: m.ProposalAssetsPage })));
const XeroAdminPage = lazy(() => import('./pages/admin/XeroAdminPage').then((m) => ({ default: m.XeroAdminPage })));
const XeroOAuthCallbackPage = lazy(() => import('./pages/admin/XeroOAuthCallbackPage').then((m) => ({ default: m.XeroOAuthCallbackPage })));
const StripeAdminPage = lazy(() => import('./pages/admin/StripeAdminPage').then((m) => ({ default: m.StripeAdminPage })));
const UtilmateAdminPage = lazy(() => import('./pages/admin/UtilmateAdminPage').then((m) => ({ default: m.UtilmateAdminPage })));
const McpConnectorPage = lazy(() => import('./pages/admin/McpConnectorPage').then((m) => ({ default: m.McpConnectorPage })));
const TenantMigrationsPage = lazy(() => import('./pages/admin/TenantMigrationsPage'));
const DevToolsPage = lazy(() => import('./pages/admin/DevToolsPage').then((m) => ({ default: m.DevToolsPage })));
const StripeOAuthCallbackPage = lazy(() => import('./pages/admin/StripeOAuthCallbackPage').then((m) => ({ default: m.StripeOAuthCallbackPage })));
const BillingSettingsLayout = lazy(() => import('./pages/admin/BillingSettingsLayout').then((m) => ({ default: m.BillingSettingsLayout })));
const AutomationsLayout = lazy(() => import('./pages/admin/AutomationsLayout').then((m) => ({ default: m.AutomationsLayout })));
const AutomationsOverviewPage = lazy(() => import('./pages/admin/AutomationsOverviewPage').then((m) => ({ default: m.AutomationsOverviewPage })));
const AutomationsTextsPage = lazy(() => import('./pages/admin/AutomationsTextsPage').then((m) => ({ default: m.AutomationsTextsPage })));
const AutomationsFlodeskPage = lazy(() => import('./pages/admin/AutomationsFlodeskPage').then((m) => ({ default: m.AutomationsFlodeskPage })));
const AutomationsSlackPage = lazy(() => import('./pages/admin/AutomationsSlackPage').then((m) => ({ default: m.AutomationsSlackPage })));
const AutobuildDashboardPage = lazy(() => import('./pages/admin/AutobuildDashboardPage').then((m) => ({ default: m.AutobuildDashboardPage })));
const UsageDashboardPage = lazy(() => import('./pages/admin/UsageDashboardPage').then((m) => ({ default: m.UsageDashboardPage })));
const BillingEmailsPage = lazy(() => import('./pages/admin/BillingEmailsPage').then((m) => ({ default: m.BillingEmailsPage })));
const BillingBankDetailsPage = lazy(() => import('./pages/admin/BillingBankDetailsPage').then((m) => ({ default: m.BillingBankDetailsPage })));
const PublicProposalViewerPage = lazy(() => import('./pages/PublicProposalViewerPage').then((m) => ({ default: m.PublicProposalViewerPage })));
const InvoicePaymentPage = lazy(() => import('./pages/public/InvoicePaymentPage').then((m) => ({ default: m.InvoicePaymentPage })));
const ClientPlanViewerPage = lazy(() => import('./pages/projects-v2/ClientPlanViewerPage').then((m) => ({ default: m.ClientPlanViewerPage })));
const SharedCableSchedulePage = lazy(() => import('./pages/projects-v2/SharedCableSchedulePage').then((m) => ({ default: m.SharedCableSchedulePage })));
const ClientVariationReviewPage = lazy(() => import('./pages/projects-v2/ClientVariationReviewPage').then((m) => ({ default: m.ClientVariationReviewPage })));
const InvoicePaymentSuccessPage = lazy(() => import('./pages/public/InvoicePaymentSuccessPage').then((m) => ({ default: m.InvoicePaymentSuccessPage })));
const DirectorsOpsPage = lazy(() => import('./pages/DirectorsOpsPage').then((m) => ({ default: m.DirectorsOpsPage })));
const DirectorsMoneyPage = lazy(() => import('./pages/DirectorsMoneyPage').then((m) => ({ default: m.DirectorsMoneyPage })));
const DirectorsTimesheetsPage = lazy(() => import('./pages/DirectorsTimesheetsPage').then((m) => ({ default: m.DirectorsTimesheetsPage })));
// The whole field shell. Kept static at first on the theory that it was on a
// critical path — it isn't: /tech is always a navigation, never the first
// paint of an office session, and leaving it static dragged 864 kB of
// src/components/tech into the entry chunk that office users never execute.
// ── Lite shell (/app) ──────────────────────────────────────────────────────
// One shell for a 1-3 person tenant. The pages are the SAME ones /home and
// /tech mount — only the navigation and layout differ, which is what keeps
// this a shell rather than a rewrite.
const LiteLayout = lazy(() => import('./pages/lite/LiteLayout').then((m) => ({ default: m.LiteLayout })));
const LiteSettingsPage = lazy(() => import('./pages/lite/LiteSettingsPage').then((m) => ({ default: m.LiteSettingsPage })));

const TechLayout = lazy(() => import('./pages/tech/TechLayout').then((m) => ({ default: m.TechLayout })));
const TechAuthGuard = lazy(() => import('./components/tech/TechAuthGuard').then((m) => ({ default: m.TechAuthGuard })));
const TechPage = lazy(() => import('./pages/TechPage').then((m) => ({ default: m.TechPage })));
const TechDashboardPage = lazy(() => import('./pages/TechDashboardPage').then((m) => ({ default: m.TechDashboardPage })));
const TechInboxPage = lazy(() => import('./pages/tech/TechInboxPage').then((m) => ({ default: m.TechInboxPage })));
const FaultConsultsPage = lazy(() => import('./pages/tech/FaultConsultsPage').then((m) => ({ default: m.FaultConsultsPage })));
const TeamSchedulePage = lazy(() => import('./pages/tech/TeamSchedulePage').then((m) => ({ default: m.TeamSchedulePage })));
const CustomersPage = lazy(() => import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const CustomerServicesPage = lazy(() => import('./pages/CustomerServicesPage').then((m) => ({ default: m.CustomerServicesPage })));
const ServiceRequestsPage = lazy(() => import('./pages/ServiceRequestsPage').then((m) => ({ default: m.ServiceRequestsPage })));
const ActionItemsPage = lazy(() => import('./pages/ActionItemsPage').then((m) => ({ default: m.ActionItemsPage })));
const PlatePage = lazy(() => import('./pages/PlatePage').then((m) => ({ default: m.PlatePage })));
const ServiceMetricsDashboardPage = lazy(() => import('./pages/ServiceMetricsDashboardPage').then((m) => ({ default: m.ServiceMetricsDashboardPage })));
const StarlinkBillingDashboardPage = lazy(() => import('./pages/StarlinkBillingDashboardPage').then((m) => ({ default: m.StarlinkBillingDashboardPage })));
const QuotesListPage = lazy(() => import('./pages/QuotesListPage').then((m) => ({ default: m.QuotesListPage })));
const PartsCatalogPage = lazy(() => import('./pages/PartsCatalogPage').then((m) => ({ default: m.PartsCatalogPage })));
const SettingsLayout = lazy(() => import('./pages/Settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })));
const SettingsGeneral = lazy(() => import('./pages/Settings/SettingsGeneral').then((m) => ({ default: m.SettingsGeneral })));
const SettingsTeam = lazy(() => import('./pages/Settings/SettingsTeam').then((m) => ({ default: m.SettingsTeam })));
const SettingsFinancials = lazy(() => import('./pages/Settings/SettingsFinancials').then((m) => ({ default: m.SettingsFinancials })));
const SettingsPlans = lazy(() => import('./pages/Settings/SettingsPlans').then((m) => ({ default: m.SettingsPlans })));
const SettingsChecklists = lazy(() => import('./pages/Settings/SettingsChecklists').then((m) => ({ default: m.SettingsChecklists })));
const ServicePlansImport = lazy(() => import('./pages/Settings/ServicePlansImport'));
const FaultPipelinePage = lazy(() => import('./pages/FaultPipelinePage').then((m) => ({ default: m.FaultPipelinePage })));
const FaultReportingPage = lazy(() => import('./pages/FaultReportingPage').then((m) => ({ default: m.FaultReportingPage })));
const SchedulePage = lazy(() => import('./pages/SchedulePage').then((m) => ({ default: m.SchedulePage })));
const DealsListPage = lazy(() => import('./pages/DealsListPage').then((m) => ({ default: m.DealsListPage })));
const DealWorkspacePage = lazy(() => import('./pages/DealWorkspacePage').then((m) => ({ default: m.DealWorkspacePage })));
const ProposalBuilderPage = lazy(() => import('./pages/ProposalBuilderPage').then((m) => ({ default: m.ProposalBuilderPage })));
const ProposalBuilderV2Page = lazy(() => import('./pages/ProposalBuilderV2Page').then((m) => ({ default: m.ProposalBuilderV2Page })));
const OpsPage = lazy(() => import('./pages/OpsPage').then((m) => ({ default: m.OpsPage })));
const JobsListPage = lazy(() => import('./pages/JobsListPage').then((m) => ({ default: m.JobsListPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const LeaveRequestsPage = lazy(() => import('./pages/LeaveRequestsPage').then((m) => ({ default: m.LeaveRequestsPage })));
const BugReportsPage = lazy(() => import('./pages/BugReportsPage').then((m) => ({ default: m.BugReportsPage })));
const WhatsNewPage = lazy(() => import('./pages/WhatsNewPage').then((m) => ({ default: m.WhatsNewPage })));
const VehiclesPage = lazy(() => import('./pages/VehiclesPage').then((m) => ({ default: m.VehiclesPage })));
const HallOfFamePage = lazy(() => import('./pages/HallOfFamePage').then((m) => ({ default: m.HallOfFamePage })));
const OtisPage = lazy(() => import('./pages/OtisPage').then((m) => ({ default: m.OtisPage })));
const AskTheoPage = lazy(() => import('./pages/AskTheoPage').then((m) => ({ default: m.AskTheoPage })));
const AskTheoSourcesPage = lazy(() => import('./pages/admin/AskTheoSourcesPage').then((m) => ({ default: m.AskTheoSourcesPage })));
const TenantTagBackfillPage = lazy(() => import('./pages/admin/TenantTagBackfillPage').then((m) => ({ default: m.TenantTagBackfillPage })));
const OfficeMapPage = lazy(() => import('./pages/OfficeMapPage').then((m) => ({ default: m.OfficeMapPage })));
const TechMapPage = lazy(() => import('./pages/tech/TechMapPage').then((m) => ({ default: m.TechMapPage })));
const TechSettingsPage = lazy(() => import('./pages/tech/TechSettingsPage').then((m) => ({ default: m.TechSettingsPage })));
const TechTimesheetPage = lazy(() => import('./pages/tech/TechTimesheetPage'));
const ProjectsV2ListPage = lazy(() => import('./pages/projects-v2/ProjectsListPage').then((m) => ({ default: m.ProjectsListPage })));
const ProjectsV2WorkspacePage = lazy(() => import('./pages/projects-v2/ProjectWorkspacePage').then((m) => ({ default: m.ProjectWorkspacePage })));
const CableSchedulePrintView = lazy(() => import('./pages/projects-v2/CableSchedulePrintView'));

/**
 * Scrolls to the top of the page on every route change.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/**
 * Keeps document.title in sync with the loaded tenant.
 *
 * dashboard.html ships with a neutral bootstrap title ('Field service')
 * so the first paint — before React mounts and before the tenant
 * subscription resolves — doesn't leak Techery branding to non-Techery
 * tenants. Once the tenant config loads, this hook overrides the static
 * title with `{tenant.companyAcronym | companyName} | Dashboard`.
 *
 * Individual pages can still override the title via their own
 * `useEffect(() => { document.title = ... })` for deep-link / context
 * titles (e.g. ProjectWorkspacePage). This hook only fires when the
 * tenant identity changes, so per-page overrides stick.
 *
 * Added May 2026 per SaaS-readiness audit.
 */
function TenantDocumentTitle() {
  const { tenant } = useTenant();
  useEffect(() => {
    if (!tenant) return;
    const acronym = tenant.companyAcronym?.trim();
    const name = tenant.companyName?.trim();
    const label = acronym || name || 'Field service';
    document.title = `${label} | Dashboard`;
  }, [tenant]);
  return null;
}

/**
 * Splat-preserving redirect from the retired Projects v2 dev-workspace base
 * (`/home/dev/projects-v2/*`) to the live `/home/projects/*` routes after the
 * June 2026 cutover. Keeps deep bookmarks working.
 */
function DevProjectsV2Redirect() {
  const params = useParams();
  const splat = params['*'] ?? '';
  return <Navigate to={`/home/projects${splat ? `/${splat}` : ''}`} replace />;
}

function App() {
  return (
    <AuthSessionProvider>
    <FinancialVisibilityProvider>
    <OtisVisibilityProvider>
    <BrowserRouter basename="/">
      <ScrollToTop />
      {/* Usage analytics — route changes → the lazily loaded capture (src/lib/usage). */}
      <UsageTracker />
      <TenantDocumentTitle />
      {/* One boundary around every route. Each lazy chunk resolves in
          milliseconds on a warm cache, so a per-route boundary would only add
          nesting without changing what the user sees. */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Landing Page - Login/Auth Screen (no auth required) */}
        <Route path="/" element={<LandingPage />} />

        {/* Email magic-link verify — outside AuthGuard because this IS the
            auth flow. The page calls verifyEmailLoginToken, gets a custom
            Firebase token, signs in, then routes to /home or /tech. */}
        <Route path="/auth/verify" element={<AuthVerifyPage />} />

        {/* ================================================================
            PUBLIC PROPOSAL VIEWER — customer-facing, NO AUTH REQUIRED.
            Token-validated server-side via the getPublicProposal CF.
            Mounted outside <AuthGuard> so customers without Firebase
            Auth can view their proposal. See:
              - docs/NATIVE_PROPOSALS_ARCHITECTURE.md §6.3
              - functions/src/proposals/getPublicProposal.ts
            ================================================================ */}
        <Route
          path="/p/:proposalId/:token"
          element={<PublicProposalViewerPage />}
        />

        {/* ================================================================
            PUBLIC PAYMENT PAGE — Phase 2.0 P2.0.5, customer-facing.
            Served from pay.thetechery.au/i/{token} (subdomain points
            at the same Firebase Hosting bundle). Mounted outside
            <AuthGuard> so customers without Firebase Auth can pay.
            Token-validated via getPublicInvoice + createPaymentIntent
            Cloud Functions.
              - functions/src/invoices/get-public-invoice.ts
              - functions/src/invoices/create-payment-intent.ts
              - functions/src/invoices/payment-token.ts (HMAC + 90d TTL)
            ================================================================ */}
        <Route path="/i/:token" element={<InvoicePaymentPage />} />
        <Route
          path="/i/:token/success"
          element={<InvoicePaymentSuccessPage />}
        />

        {/* ================================================================
            PUBLIC CLIENT PLAN VIEWER — Projects v2 Phase 4c (P4c.2),
            customer-facing. Token-validated server-side via the
            getPlanForApproval Cloud Function. The tokenId IS the auth
            (UUID v4 = 122 bits). Mounted outside <AuthGuard> so
            customers without Firebase Auth can review + approve.
              - functions/src/integrations/plan-approval.ts (CFs)
              - src/services/project-plan.service.ts (token service)
              - docs/PROJECTS_V2_MASTER_PLAN.md §4.5
            ================================================================ */}
        <Route
          path="/client/plans/:tokenId"
          element={<ClientPlanViewerPage />}
        />

        {/* ================================================================
            PUBLIC LIVE CABLE SCHEDULE — the link handed to the sparkies.
            Read the schedule as it stands and tick runs off as they are
            pulled. Several people can hold the SAME link; each types their
            name once and every tick is stamped with it.
            Token-validated server-side by the cable-share Cloud Functions;
            the tokenId IS the auth and the token doc is NOT publicly
            readable (stricter than the two viewers above — nothing here
            reads Firestore from the browser). Costs never leave the server.
              - functions/src/integrations/cable-share.ts (CFs)
              - src/services/cable-share.service.ts
              - firestore.rules — cable_schedule_tokens
            ================================================================ */}
        <Route
          path="/share/cable/:tokenId"
          element={<SharedCableSchedulePage />}
        />

        {/* ================================================================
            PUBLIC VARIATION REVIEW — Projects v2 Phase 7 (P7.10),
            customer-facing. Token-validated via the getBundleForReview
            Cloud Function. Mounted outside <AuthGuard> — customers have
            no Firebase Auth.
              - functions/src/integrations/variation-bundle-intent.ts
              - src/services/project-variation-bundle.service.ts
            ================================================================ */}
        <Route
          path="/client/variations/:tokenId"
          element={<ClientVariationReviewPage />}
        />

        {/* ================================================================
            AUTH GUARD - All routes below require sign-in (90-day session)
            ================================================================ */}
        <Route element={<AuthGuard />}>

          {/* ================================================================
              Office Dashboard - Shared layout with top nav tabs
              Dashboard | Deals | Ops | Admin
              ================================================================ */}
          <Route path="/home" element={<OfficeLayout />}>
            {/* Dashboard Hub — V2 is the dashboard. /home/v1 and /home/v2
                redirect so old bookmarks and shared links don't break. */}
            <Route index element={<OfficeDashboardV2Page />} />
            <Route path="dashboard" element={<OfficeDashboardV2Page />} />
            <Route path="v2" element={<Navigate to="/home" replace />} />
            <Route path="v1" element={<Navigate to="/home" replace />} />

            {/* Sales pillar sub-pages */}
            <Route path="deals" element={<DealsListPage />} />
            <Route path="deals/:dealId" element={<DealWorkspacePage />} />
            {/* /quote redirects to the unified Proposal Builder
                (V107.53). The legacy QuoteBuilderEnhanced was
                removed once AI scope import, versioning, and
                the service-plan picker all landed natively in
                the new builder. `?fromScope=true` is preserved
                for old bookmarks but no longer functional —
                the new auto-fire effect detects un-imported
                field scopes on its own. */}
            <Route path="deals/:dealId/quote" element={<QuoteBuilderRouteSwitch />} />
            <Route path="deals/:dealId/proposal" element={<ProposalBuilderPage />} />
            {/* Proposal Builder V2 (docs/proposal-v2/) — behind the
                proposalBuilderV2 tenant flag; admins always reach it. */}
            <Route
              path="deals/:dealId/proposal-v2"
              element={
                <RequireProposalBuilderV2>
                  <ProposalBuilderV2Page />
                </RequireProposalBuilderV2>
              }
            />
            {/* Projects — Projects v2 is now the LIVE experience (full
                cutover, June 2026). /home/projects renders the v2 list;
                :projectId/* renders the workspace (its inner <Routes> handle
                overview / timeline / stages / dwellings / plans / cable-
                schedule / variations / financials / comms / documents). Both
                read the same `projects` collection the retired
                ProjectDetailPage did, so historical projects render in the v2
                UI (empty sub-collections). The print route MUST precede the
                splat so it renders bare, without the workspace chrome. */}
            <Route path="projects" element={
              <RequireFeature feature="projects" surface="Projects">
                <ProjectsV2ListPage />
              </RequireFeature>
            } />
            <Route
              path="projects/:projectId/cable-schedule/print"
              element={
                <RequireFeature feature="projects" surface="Projects">
                  <CableSchedulePrintView />
                </RequireFeature>
              }
            />
            <Route path="projects/:projectId/*" element={
              <RequireFeature feature="projects" surface="Projects">
                <ProjectsV2WorkspacePage />
              </RequireFeature>
            } />
            <Route path="quotes" element={<QuotesListPage />} />
            <Route path="playground" element={<Navigate to="/home/quotes" replace />} />
            <Route path="parts-catalog" element={<Navigate to="/home/parts-hub" replace />} />
            <Route path="parts-hub" element={<PartsCatalogPage />} />

            {/* Ops pillar sub-pages */}
            <Route path="ops" element={<OpsPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="map" element={<OfficeMapPage />} />
            <Route path="provisioning" element={
              <RequireFeature feature="provisioning" surface="Provisioning">
                <Provisioning />
              </RequireFeature>
            } />
            <Route path="jobs" element={<JobsListPage />} />
            {/* Money-only surfaces are gated outright rather than masked —
                a billing screen with every amount replaced by `••••` is a
                shell of empty rows, not a usable page. */}
            <Route
              path="billing"
              element={
                <FinancialAccessGate surface="Billing">
                  <BillingPage />
                </FinancialAccessGate>
              }
            />
            <Route path="faults" element={
              <ErrorBoundary>
                <FaultPipelinePage />
              </ErrorBoundary>
            } />
            <Route path="faults/reports" element={
              <ErrorBoundary>
                <FaultReportingPage />
              </ErrorBoundary>
            } />
            {/* Directors — restricted access. $$ at /directors, Ops at /directors/ops */}
            <Route path="directors" element={
              <ErrorBoundary>
                <DirectorsMoneyPage />
              </ErrorBoundary>
            } />
            <Route path="directors/ops" element={
              <ErrorBoundary>
                <DirectorsOpsPage />
              </ErrorBoundary>
            } />
            <Route path="directors/timesheets" element={
              <ErrorBoundary>
                <DirectorsTimesheetsPage />
              </ErrorBoundary>
            } />

            {/* Legacy redirects to new Directors URLs */}
            <Route path="reports/directors" element={<Navigate to="/home/directors/ops" replace />} />

            {/* Legacy dev-workspace paths → the live /home/projects routes
                (full cutover, June 2026). Splat-preserving redirect so deep
                bookmarks (e.g. /home/dev/projects-v2/:id/plans/:planId)
                survive the move. */}
            <Route path="dev/projects-v2/*" element={<DevProjectsV2Redirect />} />

            {/* Admin pillar sub-pages */}
            <Route path="admin" element={<AdminPage />} />
            {/* Autobuild dashboard — directors only (DirectorAccessGate inside the page). */}
            <Route path="admin/autobuild" element={<AutobuildDashboardPage />} />
            {/* Usage analytics — owners only (DirectorAccessGate inside the page). */}
            <Route path="admin/usage" element={<UsageDashboardPage />} />
            <Route path="leave" element={<LeaveRequestsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="services" element={
              <RequireFeature feature="customer-services" surface="Customer services">
                <CustomerServicesPage />
              </RequireFeature>
            } />
            <Route path="service-requests" element={<ServiceRequestsPage />} />
            <Route path="action-items" element={<ActionItemsPage />} />
            <Route path="plate" element={<PlatePage />} />
            <Route path="bugs" element={<BugReportsPage />} />
            <Route path="whats-new" element={<WhatsNewPage />} />
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="hall-of-fame" element={<HallOfFamePage />} />
            <Route path="otis" element={<OtisPage />} />
            <Route path="ask" element={<AskTheoPage />} />

            {/* Consolidated proposal-settings hub (Step 6) — single
                page with tabbed nav for templates / assets / emails /
                branding / sender domain. Old standalone routes
                redirect into the new tabs so existing bookmarks
                survive. */}
            <Route path="admin/proposal-settings" element={<ProposalSettingsLayout />}>
              <Route index element={<Navigate to="templates" replace />} />
              <Route path="templates" element={<ProposalTemplatesListPage />} />
              <Route path="templates/:templateId" element={<ProposalTemplateEditPage />} />
              <Route path="assets" element={<ProposalAssetsPage />} />
              <Route path="emails" element={<ProposalEmailsPage />} />
              <Route path="branding" element={<ProposalBrandingPage />} />
              <Route path="sender" element={<ProposalSenderDomainPage />} />
            </Route>
            {/* Legacy redirects — keep bookmarks alive. */}
            <Route
              path="admin/proposal-templates"
              element={<Navigate to="/home/admin/proposal-settings/templates" replace />}
            />
            <Route
              path="admin/proposal-templates/:templateId"
              element={<RedirectToTemplateEditor />}
            />
            <Route
              path="admin/proposal-assets"
              element={<Navigate to="/home/admin/proposal-settings/assets" replace />}
            />
            <Route
              path="admin/proposal-sender-domain"
              element={<Navigate to="/home/admin/proposal-settings/sender" replace />}
            />
            {/* Platform-admin-only routes (isAdmin claim via RequireAdmin).
                Email Analytics is here for data protection — it reads
                `email_events`, which holds every customer's recipientEmail
                across all outbound flows (audit CRITICAL fix). The rest is
                the /home/admin/dev toolset — internal migrations, backfills,
                and importers moved off the everyday admin hub (admin-home
                overhaul, Aug 2026). The tenant-facing integration pages
                (xero / stripe / utilmate / mcp-connector) stay outside
                deliberately: they're everyday admin surfaces whose sensitive
                actions are gated server-side. */}
            <Route element={<RequireAdmin />}>
              <Route path="admin/email-analytics" element={<EmailAnalyticsPage />} />
              {/* Developer-tools hub — the index page for the internal
                  tooling below. Linked from AdminPage for admins only. */}
              <Route path="admin/dev" element={<DevToolsPage />} />
              {/* Ask Theo — historical source uploader (admin-only; the page
                  reads rag_sources + drives the ingest/delete callables, all
                  admin-gated server-side). */}
              <Route path="admin/ask-theo-sources" element={<AskTheoSourcesPage />} />
              {/* Tenant-tag backfill — admin-only GUI for the
                  backfillTenantCoverage callable (which re-verifies admin
                  server-side). Stamps the canonical tenantId onto records
                  missing it. Dry-run-first, idempotent. */}
              <Route path="admin/tenant-tag-backfill" element={<TenantTagBackfillPage />} />
              {/* Phase 4 BP→native migration tooling */}
              <Route path="admin/proposal-migration" element={<ProposalMigrationPage />} />
              <Route path="admin/proposal-comparison" element={<ProposalComparisonPage />} />
              {/* Tenant go-live migrations (GUI for the backfill/ownerIds scripts) */}
              <Route path="admin/tenant-migrations" element={<TenantMigrationsPage />} />
            </Route>
            {/* Xero integration — Phase 1 (P1.2) */}
            <Route path="admin/xero" element={<XeroAdminPage />} />
            <Route path="admin/xero/callback" element={<XeroOAuthCallbackPage />} />
            {/* Stripe Connect — Phase 2.0 (P2.0.2) */}
            <Route path="admin/stripe" element={<StripeAdminPage />} />
            <Route
              path="admin/stripe/connect/callback"
              element={<StripeOAuthCallbackPage />}
            />
            {/* Utilmate recurring-billing integration (Phase 2) */}
            <Route path="admin/utilmate" element={
              <RequireFeature feature="utilmate" surface="Utilmate">
                <UtilmateAdminPage />
              </RequireFeature>
            } />
            {/* Claude MCP connector — token management for the theoMcp endpoint */}
            <Route path="admin/mcp-connector" element={<McpConnectorPage />} />
            {/* Billing settings hub — invoice / receipt / reminder
                email copy editor. Sibling to proposal-settings; shares
                the `tenant_email_templates` Firestore doc. */}
            <Route path="admin/billing-settings" element={<BillingSettingsLayout />}>
              <Route index element={<Navigate to="bank" replace />} />
              <Route path="emails" element={<BillingEmailsPage />} />
              <Route path="bank" element={<BillingBankDetailsPage />} />
            </Route>
            {/* Automations hub — editable customer SMS templates + test send,
                Flodesk/email automation map, and config-health overview. */}
            <Route path="admin/automations" element={<AutomationsLayout />}>
              <Route index element={<AutomationsOverviewPage />} />
              <Route path="texts" element={<AutomationsTextsPage />} />
              <Route path="flodesk" element={<AutomationsFlodeskPage />} />
              {/* Slack channel routing — admin-only data (rules-enforced) */}
              <Route path="slack" element={<AutomationsSlackPage />} />
            </Route>
          </Route>

          {/* Convenience alias — /admin/proposal-templates → /home/admin/proposal-templates */}
          <Route path="/admin/proposal-templates" element={<Navigate to="/home/admin/proposal-templates" replace />} />

          {/* Legacy redirects → now go to /home */}
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/sales" element={<Navigate to="/home/deals" replace />} />
          <Route path="/sales/playground" element={<Navigate to="/home/quotes" replace />} />
          <Route path="/ops" element={<Navigate to="/home/ops" replace />} />
          <Route path="/pipeline-settings" element={<Navigate to="/home/deals" replace />} />
          <Route path="/sales-legacy" element={<Navigate to="/home/deals" replace />} />
          <Route path="/ops-legacy" element={<Navigate to="/home/ops" replace />} />

          {/* Standalone redirects → nested under /home */}
          <Route path="/schedule" element={<Navigate to="/home/schedule" replace />} />
          <Route path="/faults" element={<Navigate to="/home/faults" replace />} />
          <Route path="/deals" element={<Navigate to="/home/deals" replace />} />
          <Route path="/deals/:dealId" element={<DealRedirect />} />
          <Route path="/projects" element={<Navigate to="/home/projects" replace />} />

          {/* Provisioning — redirect to nested route under /home */}
          <Route path="/provisioning" element={<Navigate to="/home/provisioning" replace />} />

          {/* Quote Review - PreSite parts review and approval */}
          <Route path="/quote-review" element={<QuoteReviewPage />} />

          {/* Placeholder routes for other navigation tiles */}
          <Route path="/operations" element={<PlaceholderPage title="Operations Map" />} />
          <Route path="/pipeline" element={<Navigate to="/home/deals" replace />} />

          {/* Standalone redirects for pages now nested under /home */}
          <Route path="/customers" element={<Navigate to="/home/customers" replace />} />
          <Route path="/services" element={<Navigate to="/home/services" replace />} />
          <Route path="/playground" element={<Navigate to="/home/quotes" replace />} />

          {/* Reports - Service Analytics Dashboard. MRR/ARPU end to end, so
              it's financial-visibility gated rather than masked. */}
          <Route
            path="/reports/services"
            element={
              <RequireFeature feature="service-metrics" surface="Service metrics">
                <FinancialAccessGate surface="Service metrics">
                  <ServiceMetricsDashboardPage />
                </FinancialAccessGate>
              </RequireFeature>
            }
          />

          {/* Starlink Billing Management */}
          <Route
            path="/services/starlink-billing"
            element={
              <RequireFeature feature="starlink" surface="Starlink billing">
                <FinancialAccessGate surface="Starlink billing">
                  <StarlinkBillingDashboardPage />
                </FinancialAccessGate>
              </RequireFeature>
            }
          />


          {/* ── Lite shell ────────────────────────────────────────────────
              A Lite tenant gets ONE shell at /app instead of the /home + /tech
              split. Same page components, eight destinations, phone-first nav.
              RequireLite bounces a Pro tenant to /home rather than showing them
              an eight-item nav over an app that has 82 routes.

              Feature gating still applies per page — a Lite tenant without the
              billing pack has no Billing tab AND no /app/billing route. */}
          <Route path="/app" element={<AuthGuard />}>
            <Route
              element={
                <RequireLite>
                  <LiteLayout />
                </RequireLite>
              }
            >
            <Route index element={<ErrorBoundary><TechPage /></ErrorBoundary>} />
            <Route path="quotes" element={
              <RequireFeature feature="deals" surface="Quotes"><DealsListPage /></RequireFeature>
            } />
            <Route path="quotes/:dealId" element={
              <RequireFeature feature="deals" surface="Quotes"><DealWorkspacePage /></RequireFeature>
            } />
            <Route path="quotes/:dealId/proposal" element={
              <RequireFeature feature="proposals" surface="Proposals"><ProposalBuilderPage /></RequireFeature>
            } />
            <Route path="schedule" element={
              <RequireFeature feature="schedule" surface="Schedule"><SchedulePage /></RequireFeature>
            } />
            <Route path="jobs" element={
              <RequireFeature feature="jobs" surface="Jobs"><JobsListPage /></RequireFeature>
            } />
            <Route path="callbacks" element={
              <RequireFeature feature="faults" surface="Faults"><FaultPipelinePage /></RequireFeature>
            } />
            <Route path="customers" element={
              <RequireFeature feature="customers" surface="Customers"><CustomersPage /></RequireFeature>
            } />
            <Route path="billing" element={
              <RequireFeature feature="billing" surface="Billing">
                <FinancialAccessGate surface="Billing"><BillingPage /></FinancialAccessGate>
              </RequireFeature>
            } />
            <Route path="settings" element={<LiteSettingsPage />} />
            {/* Anything else under /app is a stale link, not a 404 page. */}
            <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>
          </Route>

          {/* Technician Routes - TechAuthGuard adds parallel 90-day re-auth on top */}
          <Route path="/tech" element={<TechAuthGuard />}>
            <Route element={<TechLayout />}>
              <Route index element={<ErrorBoundary><TechPage /></ErrorBoundary>} />
              <Route path="dashboard" element={<ErrorBoundary><TechDashboardPage /></ErrorBoundary>} />
              <Route path="inbox" element={<ErrorBoundary><TechInboxPage /></ErrorBoundary>} />
              <Route path="consults" element={<ErrorBoundary><FaultConsultsPage /></ErrorBoundary>} />
              <Route path="timesheet" element={<ErrorBoundary><TechTimesheetPage /></ErrorBoundary>} />
              <Route path="team" element={<ErrorBoundary><TeamSchedulePage /></ErrorBoundary>} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="map" element={<TechMapPage />} />
              <Route path="settings" element={<TechSettingsPage />} />
            </Route>
          </Route>

          {/* Settings Module - Nested Routes */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route path="general" element={<SettingsGeneral />} />
            <Route path="team" element={<SettingsTeam />} />
            {/* Who can see currency amounts. Readable by any signed-in user
                (so they can see who to ask); the switches enforce admin. */}
            <Route path="financials" element={<SettingsFinancials />} />
            <Route path="plans" element={
              <RequireFeature feature="service-plans" surface="Service plans">
                <SettingsPlans />
              </RequireFeature>
            } />
            <Route path="import-plans" element={
              <RequireFeature feature="service-plans" surface="Service plans">
                <ServicePlansImport />
              </RequireFeature>
            } />
            <Route path="checklists" element={<SettingsChecklists />} />
          </Route>

          {/* Seed/test data — writes real records, so platform admins only.
              Linked from /home/admin/dev. */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin/seed" element={<AdminSeedPage />} />
            <Route path="/admin-seed" element={<AdminSeedPage />} />
          </Route>

          {/* Platform console — the "zero tenant" mini-app (overview /
              tenants / billing / security). Authorization is entirely
              server-side: a platform_admins/{uid} grant PLUS a step-up
              unlock (platform password + emailed code) gate every callable;
              the layout renders denial / lock screens for everyone else.
              Deliberately outside the office shell — platform work is not
              tenant work. */}
          <Route path="/platform" element={<PlatformLayout />}>
            <Route index element={<PlatformOverviewPage />} />
            <Route path="tenants" element={<PlatformTenantsPage />} />
            <Route path="tenants/:tenantId" element={<PlatformTenantDetailPage />} />
            <Route path="usage" element={<PlatformUsagePage />} />
            <Route path="billing" element={<PlatformBillingPage />} />
            <Route path="security" element={<PlatformSecurityPage />} />
          </Route>

        </Route>{/* End AuthGuard */}

      </Routes>
      </Suspense>

      {/* Floating Otis launcher — shown on every authenticated app screen
          (office + tech); hides itself on landing / auth / public / dev
          routes and the full Otis page. */}
      <OtisWidget />
    </BrowserRouter>
    </OtisVisibilityProvider>
    </FinancialVisibilityProvider>
    </AuthSessionProvider>
  );
}

/**
 * Redirects /deals/:dealId → /home/deals/:dealId preserving the dealId param
 */
function DealRedirect() {
  const { dealId } = useParams();
  return <Navigate to={`/home/deals/${dealId}`} replace />;
}

/**
 * `/home/deals/:dealId/quote` route — back-compat redirect to the
 * unified Proposal Builder (V107.53). Kept around because the deal
 * timeline + QuoteSummaryCard still link to `/quote`, and older
 * bookmarks may exist. Preserves the query string so legacy
 * `?fromScope=true` continues to behave intuitively (the new
 * builder's auto-fire effect detects un-imported field scopes on
 * its own, but the param remains visually informative).
 *
 * The legacy `QuoteBuilderEnhanced` component was deleted in the
 * QW1 cleanup once AI scope import, quote versioning, and the
 * service-plan picker had all landed natively in the new builder.
 */
function QuoteBuilderRouteSwitch() {
  const { dealId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/home/deals/${dealId}/proposal${location.search}`}
      replace
    />
  );
}

/**
 * Legacy `/home/admin/proposal-templates/:templateId` URLs land here
 * and bounce into the new hub's editor route while preserving the
 * `:templateId` param. Bookmarks survive Step 6's consolidation.
 */
function RedirectToTemplateEditor() {
  const { templateId } = useParams();
  return (
    <Navigate
      to={`/home/admin/proposal-settings/templates/${templateId ?? ''}`}
      replace
    />
  );
}

/**
 * Placeholder page for routes not yet implemented
 */
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        <div className="text-blue-500 mb-4">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-6">This page is coming soon.</p>
        <a
          href="/"
          className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  );
}

export default App;
